import React from 'react';
import { StarIcon } from 'lucide-react';
import { Avatar } from './Avatar';
import { avatarColors } from '../data/coaches';
import type { CoachProfile, Testimonial } from '../data/testimonials';
type TestimonialsProps = {
  testimonials: Testimonial[];
  profiles?: CoachProfile[];
  title?: string;
  subtitle?: string;
  background?: 'white' | 'cream';
};

/** Renders nothing until data/testimonials.ts holds real buyer reviews. */
export function Testimonials({
  testimonials,
  profiles = [],
  title = 'What speaking teachers are saying',
  subtitle = 'From teachers, coaches and club leaders running speaking sessions every week.',
  background = 'white'
}: TestimonialsProps) {
  if (testimonials.length === 0) return null;
  const bg = background === 'white' ? 'bg-white' : 'bg-cloud';
  return <section aria-label={title} className={`w-full text-ink ${bg}`} data-id="element-375" data-mp="v9ydr4zhpj73">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-376" data-mp="v72ftgnn6uin">
        <div className="text-center" data-id="element-377" data-mp="qavbfsaw9r2t">
          <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-coral-deep" data-id="element-378" data-mp="h5y6q955730z">Teacher to teacher</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-ink sm:text-5xl" data-id="element-379" data-mp="m5kkcjovulab">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-slate" data-id="element-380" data-mp="78aatfvsvph8">{subtitle}</p>
        </div>

        {profiles.length > 0 && <div className="mt-9 grid gap-6 border-y border-cloud-dark py-7 sm:grid-cols-2 sm:gap-10" data-id="element-381" data-mp="c7myu3yer2le">
            {profiles.map((profile, i) => <figure key={profile.name} className="flex gap-4" data-id="element-382" data-mp="14vj2jsme9uro" data-mpi={i}>
                <Avatar name={profile.name} color={avatarColors[(i + 1) % avatarColors.length]} className="h-12 w-12 flex-none" data-id="element-383" data-mp="14ur6yhssrx1e" />
                <div data-id="element-384" data-mp="v5ps4uxznlp0">
                  <figcaption data-id="element-385" data-mp="v57ld4dp9p2u">
                    <span className="block font-display text-lg font-extrabold leading-tight text-ink" data-id="element-386" data-mp="v4em0oh2m1uq">{profile.name}</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-coral-deep" data-id="element-387" data-mp="1it7c44b0hfhf">{profile.role}</span>
                  </figcaption>
                  <blockquote className="mt-2 text-sm leading-relaxed text-slate" data-id="element-388" data-mp="1xi92j8t01dd0">“{profile.bio}”</blockquote>
                </div>
              </figure>)}
          </div>}

        <div className={`mt-9 grid gap-4 ${testimonials.length === 1 ? 'mx-auto max-w-md' : 'sm:grid-cols-2 lg:grid-cols-3'}`} data-id="element-389" data-mp="1ogsz5l0lqbia">
          {testimonials.map((item, i) => <figure key={item.author} className="flex flex-col rounded-2xl border border-cloud-dark bg-white p-5 shadow-soft" data-id="element-390" data-mp="1ek6g91ru5uiw" data-mpi={i}>
              <div className="flex items-center gap-2.5" data-id="element-391" data-mp="19r3ngvuvup3r">
                <Avatar name={item.author} src={item.avatar} color={avatarColors[i % avatarColors.length]} className="h-11 w-11" data-id="element-392" data-mp="14sk1ivgahdbe" />
                <span className="flex gap-0.5" role="img" aria-label="Five out of five" data-id="element-393" data-mp="10tb33dab0r9i">
                  {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="h-3.5 w-3.5 fill-amber text-amber" aria-hidden="true" data-id="element-394" data-mp="qtke1r4ij4gt" />)}
                </span>
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed text-ink" data-id="element-395" data-mp="32gnvizl8nt71">“{item.quote}”</blockquote>
              <figcaption className="mt-auto pt-4 text-xs text-slate" data-id="element-396" data-mp="37i3f0n7rprnb">
                <span className="block text-[13px] font-semibold text-ink" data-id="element-397" data-mp="3cgep8z1z6462">{item.author}</span>
                {item.role}
              </figcaption>
            </figure>)}
        </div>
      </div>
    </section>;
}